/**
 * Reports Manager component for Pro users
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import reportService from '../services/reportService';

const ReportsManager = ({ userSubscription }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog states
  const [createReportOpen, setCreateReportOpen] = useState(false);
  const [reportType, setReportType] = useState('insurance');
  const [generating, setGenerating] = useState(false);

  const isProUser = ['pro', 'organizer', 'mover_pro'].includes(userSubscription?.plan);

  useEffect(() => {
    if (isProUser) {
      loadReports();
    } else {
      setLoading(false);
    }
  }, [isProUser]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await reportService.getUserReports();
      setReports(response.reports || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setError('');
      setGenerating(true);
      
      let response;
      if (reportType === 'insurance') {
        response = await reportService.generateInsuranceReport();
      } else if (reportType === 'moving') {
        response = await reportService.generateMovingReport();
      }
      
      setSuccess('Report generated successfully!');
      setCreateReportOpen(false);
      loadReports();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExportReport = async (reportId) => {
    try {
      setError('');
      const response = await reportService.exportReport(reportId);
      
      // Create download link
      if (response.download_url) {
        window.open(response.download_url, '_blank');
      } else {
        setSuccess('Export started. Download will be available shortly.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }
    
    try {
      setError('');
      await reportService.deleteReport(reportId);
      setSuccess('Report deleted successfully!');
      loadReports();
    } catch (err) {
      setError(err.message);
    }
  };

  const getReportIcon = (type) => {
    switch (type) {
      case 'insurance': return <SecurityIcon />;
      case 'moving': return <LocalShippingIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'generating': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const filterReports = (type) => {
    if (type === 'all') return reports;
    return reports.filter(report => report.report_type === type);
  };

  const getTabReports = () => {
    switch (tabValue) {
      case 0: return filterReports('all');
      case 1: return filterReports('insurance');
      case 2: return filterReports('moving');
      default: return reports;
    }
  };

  if (!isProUser) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Insurance & Moving Reports
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Professional reports are available with Pro subscription. Generate detailed insurance claims and moving inventory reports.
          </Alert>
          <Button variant="contained" color="primary">
            Upgrade to Pro
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Reports & Exports
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateReportOpen(true)}
          >
            Generate Report
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label={`All Reports (${reports.length})`} />
          <Tab label={`Insurance (${filterReports('insurance').length})`} />
          <Tab label={`Moving (${filterReports('moving').length})`} />
        </Tabs>

        {getTabReports().length === 0 ? (
          <Alert severity="info">
            No reports found. Generate your first report to get started.
          </Alert>
        ) : (
          <List>
            {getTabReports().map((report) => (
              <ListItem key={report.id} divider>
                <Box display="flex" alignItems="center" mr={2}>
                  {getReportIcon(report.report_type)}
                </Box>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {report.title}
                      </Typography>
                      <Chip
                        label={report.status}
                        size="small"
                        color={getStatusColor(report.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Type: {report.report_type} • Items: {report.items_count || 0}
                        {report.total_value && ` • Value: $${report.total_value}`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(report.created_at).toLocaleDateString()}
                        {report.generated_at && ` • Generated: ${new Date(report.generated_at).toLocaleDateString()}`}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box display="flex" gap={1}>
                    {report.status === 'completed' && (
                      <IconButton
                        edge="end"
                        onClick={() => handleExportReport(report.id)}
                        title="Export as CSV"
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteReport(report.id)}
                      title="Delete report"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Generate Report Dialog */}
        <Dialog open={createReportOpen} onClose={() => setCreateReportOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label="Report Type"
                  >
                    <MenuItem value="insurance">
                      <Box display="flex" alignItems="center" gap={1}>
                        <SecurityIcon fontSize="small" />
                        Insurance Claim Report
                      </Box>
                    </MenuItem>
                    <MenuItem value="moving">
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalShippingIcon fontSize="small" />
                        Moving Inventory Report
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  {reportType === 'insurance' 
                    ? 'Generate a detailed report for insurance claims including item values, serial numbers, and receipts.'
                    : 'Create an inventory list organized by location for moving companies and logistics.'
                  }
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateReportOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGenerateReport}
              variant="contained"
              disabled={generating}
              startIcon={generating ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ReportsManager;
